import h from 'hyperscript';
import trashIcon from '@disy/cadenza-icons/trash.svg';

import { FormGroup } from 'ui/form-group/form-group';
import { Switch } from 'ui/switch/switch';
import { createAnchorButton, createIconButton } from 'ui/button/button';
import { Select } from 'ui/select/select';

import { AttributeType, isNumeric } from 'cadenza/repository/attribute-type';
import i18n from 'cadenza/map/feature-attributes-panel/messages.properties';
import { icon } from 'cadenza/utils/icon/icon';
import { DatePicker } from 'cadenza/conditions/date-picker/date-picker';
import { on } from 'cadenza/utils/event-util';
import { parseDateWithJavaFormat } from 'cadenza/utils/date-time/date-format';

export const InputType = {
  DATE: 'DATE',
  SELECT: 'SELECT',
  SIMPLE: 'SIMPLE',
  URL: 'URL'
};

const DELETE_VALUES_CLASS = 'is-delete-values';

export class FeatureAttributeFormGroup extends FormGroup {

  // readonly configuration data
  #attributeMetaData;
  #initialValue;
  #inputType;

  // readonly inner elements
  #attributeToggleButton;
  #deleteButton;
  #formControl;
  #labelElement;
  #resetButton;

  // helper flags
  #isDeleteValues;
  #isDisabled;

  // readonly helper flags
  #isMultipleFeatures;

  constructor ({ attributeMetaData, isMultipleFeatures, initialValue }) {
    super();

    this.#attributeMetaData = attributeMetaData;
    this.#isMultipleFeatures = isMultipleFeatures;
    this.#initialValue = initialValue;
    if (this.#attributeMetaData.labeledValues) {
      this.#inputType = InputType.SELECT;
    } else if (AttributeType.DATE === this.#attributeMetaData.attributeType) {
      this.#inputType = InputType.DATE;
    } else if (AttributeType.URL === this.#attributeMetaData.attributeType) {
      this.#inputType = InputType.URL;
    } else {
      this.#inputType = InputType.SIMPLE;
    }
  }

  connectedCallback () {
    this.classList.add('d-feature-attribute-form-group');
    if (this.#isMultipleFeatures) {
      this.#isDisabled = true;
      this.setAttribute('aria-disabled', this.#isDisabled);
    }

    this._createAttributeResetButton();
    this._createFormGroupControl();
    this._setFormControlPlaceholder();

    this.#attributeToggleButton = null;
    if (this.inputType !== AttributeType.URL) {
      this._createDeleteAttributeButton();

      if (this.isMultipleFeatures) {
        this._createAttributeToggle();
      }
    }
    this._createFormGroupLabel();

    this.append(
      this.#labelElement,
      h('.input-wrapper', [
        this.#formControl,
        this.#deleteButton
      ])
    );

    super.connectedCallback();
  }

  get attributeMetaData () {
    return this.#attributeMetaData;
  }

  get initialValue () {
    return this.#initialValue;
  }

  get inputType () {
    return this.#inputType;
  }

  get attributeToggleButton () {
    return this.#attributeToggleButton;
  }

  get deleteButton () {
    return this.#deleteButton;
  }

  get formControl () {
    return this.#formControl;
  }

  get labelElement () {
    return this.#labelElement;
  }

  get resetButton () {
    return this.#resetButton;
  }

  get isDeleteValues () {
    return this.#isDeleteValues;
  }

  get isDisabled () {
    return this.#isDisabled;
  }

  get isMultipleFeatures () {
    return this.#isMultipleFeatures;
  }

  // content creating functions
  _createFormGroupLabel () {
    this.#labelElement = h('label', {
      htmlFor: this.#attributeMetaData.name
    }, [
      this.#attributeToggleButton,
      this.#attributeMetaData.printName,
      this.#resetButton
    ]);
  }

  _createFormGroupControl () {
    if (this.#inputType === InputType.SELECT) {
      this._createSelect();
    } else if (this.#inputType === InputType.URL) {
      this._createDisabledField();
    } else if (this.#inputType === InputType.DATE) {
      this._createDatePicker();
    } else {
      this._createInput();
    }
  }

  _createDisabledField () {
    return  h('span.form-control', {
      id: this.#attributeMetaData.name,
      title: this.#attributeMetaData.printName,
      disabled: true
    }, this._initialValueToString());
  }

  _createAttributeToggle () {
    this.#attributeToggleButton = new Switch();
    this.#attributeToggleButton.classList.add('float-left');
    this.#attributeToggleButton.checked = false;
    this.#isDisabled = !this.#attributeToggleButton.checked;
    this.#attributeToggleButton.addEventListener('change', () => {
      this.#isDisabled = !this.#attributeToggleButton.checked;
      this._toggleDeleteAttributeButton(false);
      this._setFormControlPlaceholder();

      if (this.#inputType === InputType.DATE) {
        this.#formControl.enabled = this.#attributeToggleButton.checked;
      } else {
        this.#formControl.disabled = !this.#attributeToggleButton.checked;
      }
      this.#attributeToggleButton.parentElement.parentElement.setAttribute(
        'aria-disabled',
        (!this.#attributeToggleButton.checked).toString()
      );
      this.#deleteButton.hidden =
        this.#attributeMetaData.mandatory || !this.#attributeToggleButton.checked;
      this.#resetButton.hidden = true;
    });
  }

  _createAttributeResetButton () {
    this.#resetButton = createAnchorButton(i18n('reset'), {
      hidden: true,
      onclick: (event) => {
        event.stopImmediatePropagation();

        if (this.#inputType === InputType.DATE) {
          this.#formControl.values = {
            start: this._initialValueToString()
          };
        } else if (this.#inputType === InputType.SELECT) {
          this.#formControl.selection = this._initialValueToString();
        } else {
          this.#formControl.value = this._initialValueToString();
        }

        this._toggleDeleteAttributeButton(false);
        this.#resetButton.hidden = true;
      }
    });
  }

  _createDeleteAttributeButton () {
    this.#isDeleteValues = false;
    this.#deleteButton = createIconButton(
      icon(trashIcon),
      i18n('deleteValues.title', { toDelete: i18n('deleteValues.no') }),
      {
        variant: 'borderless',
        size: 'm',
        styleClass: [ 'delete-values-button' ],
        attrs: {
          'aria-checked': false
        },
        onclick: () => {
          this._toggleDeleteAttributeButton(!this.#isDeleteValues);
        }
      }
    );

    this.#deleteButton.hidden = this.isMultipleFeatures || this.#attributeMetaData.mandatory;
  }

  _toggleDeleteAttributeButton (isDeleteAttributeButtonOn) {
    if (this.#deleteButton === null) {
      return;
    }
    this.#isDeleteValues = isDeleteAttributeButtonOn;
    this.#deleteButton.setAttribute('aria-checked', this.#isDeleteValues);
    this._toggleFormGroupControlToDelete();
    if (this.#isDeleteValues) {
      this.#deleteButton.title =
        i18n('deleteValues.title', { toDelete: i18n('deleteValues.yes') });
      this.classList.add(DELETE_VALUES_CLASS);
    } else {
      this.#deleteButton.title =
        i18n('deleteValues.title', { toDelete: i18n('deleteValues.no') });
      this.classList.remove(DELETE_VALUES_CLASS);
    }
    this.#resetButton.hidden = !this.#isDeleteValues;
  }

  _toggleFormGroupControlToDelete () {
    if (this.#inputType === InputType.DATE) {
      this.#formControl.values = this.#isDeleteValues
        ? null : { start: this._initialValueToString() };
      this.#formControl.input.nextElementSibling.hidden = this.#isDeleteValues;
      this.#formControl.enabled = !this.#isDeleteValues;
    } else {
      const formGroupControlValue = this.#isDeleteValues
        ? '' : this._initialValueToString();
      if (this.#inputType === InputType.SELECT) {
        this.#formControl.selection = formGroupControlValue;
      } else {
        this.#formControl.value = formGroupControlValue;
      }

      this.#formControl.disabled = this.#isDeleteValues;
    }
    this._setFormControlPlaceholder();
  }

  _createDatePicker () {
    this.#formControl = new DatePicker({
      relativeDateSuggestions: [],
      dateFormat: this.#attributeMetaData.formatPattern || i18n('dateFormat'),
      inputId: this.#attributeMetaData.name,
      isRequired: this.#attributeMetaData.mandatory,
      autoOpen: true,
      modalStyleClass: 'feature-edit-date-picker-modal',
      title: i18n('datePickerTitle')
    });
    const parsedDate = typeof this.#initialValue === 'string'
      ? parseDateWithJavaFormat(this.#initialValue, this.#attributeMetaData.formatPattern || i18n('dateFormat')).getTime()
      : this.#initialValue;
    this.#formControl.values = {
      start: parsedDate
    };
    this.#formControl.enabled = !this.#isMultipleFeatures;

    on(this.#formControl, 'date-selected', () => {
      this.resetButton.hidden = new Date(this.#formControl.values.start).getTime()
        === new Date(this.#initialValue ?? null).getTime();
    });
  }

  _createInput () {
    const numberRangeConfiguration = this.#attributeMetaData.numberRange ? {
      min: this.#attributeMetaData.numberRange.min,
      max: this.#attributeMetaData.numberRange.max
    } : {};
    this.#formControl = h('input.form-control', {
      id: this.#attributeMetaData.name,
      required: this.#attributeMetaData.mandatory,
      title: this.#attributeMetaData.printName,
      value: this._initialValueToString(),
      disabled: this.#isMultipleFeatures,
      type: (isNumeric(this.#attributeMetaData.attributeType) || this.#attributeMetaData.attributeType === AttributeType.DURATION)
        ? 'number' : 'text',
      ...numberRangeConfiguration,
      onkeyup: () => {
        this.#resetButton.hidden = this.#formControl.value === this._initialValueToString();
      }
    });
  }

  _createSelect () {
    this.#formControl = new Select({
      data: this._createSelectOptions(),
      required: this.#attributeMetaData.mandatory,
      templateSelection: (item) => {
        return this._createSelectTemplateSelection(item);
      }
    });
    this.#formControl.id = this.#attributeMetaData.name;
    this.#formControl.disabled = this.#isMultipleFeatures;
    this.#formControl.selection = this._initialValueToString();
    this.#formControl.addEventListener('change', () => {
      this.#resetButton.hidden =
        this.#formControl.selection[0].id === this._initialValueToString();
    });
  }

  _createSelectOptions () {
    const selectOptions = [];
    if (!this.#attributeMetaData.mandatory) {
      selectOptions.push({
        id: '',
        text: ''
      });
    }

    this.#attributeMetaData.labeledValues.forEach((labeledValue) => {
      selectOptions.push({
        id: labeledValue.value,
        text: labeledValue.label || labeledValue.value
      });
    });

    return selectOptions;
  }

  _chooseMessage (item) {
    if (!this.#isDeleteValues) {
      return item.text;
    }
    return this.#isMultipleFeatures
      ? i18n('placeholder.message.deleteAllValues')
      : i18n('placeholder.message.deleteSingleValue');
  }

  _createSelectTemplateSelection (item) {
    if (item.id === '') {
      return h(
        '.select2-selection__placeholder',
        this._chooseMessage(item)
      );
    }

    return item.text;
  }

  _setFormControlPlaceholder () {
    if (this.#inputType === InputType.DATE) {
      this.#formControl.input.setAttribute(
        'placeholder',
        this._getPlaceholderText()
      );
    } else {
      this.#formControl.setAttribute(
        'placeholder',
        this._getPlaceholderText()
      );
    }
  }

  _initialValueToString () {
    return (this.#initialValue ?? '').toString();
  }

  _getPlaceholderText () {
    if (this.#isDeleteValues) {
      return this.#isMultipleFeatures
        ? i18n('placeholder.message.deleteAllValues')
        : i18n('placeholder.message.deleteSingleValue');
    } else {
      return this.#isDisabled && this._isUndefinedValueInBulkEditMode()
        ? i18n('placeholder.message.differentValues') : '';
    }
  }

  getInputValue () {
    if (this.#isDeleteValues) {
      return null;
    } else {
      switch (this.#inputType) {
        case InputType.DATE:
          return this.#formControl.values.start;
        case InputType.SELECT:
          return this.#formControl.selection[0]?.id ?? null;
        case InputType.URL:
          return this.#initialValue;
        default:
          return this.#formControl.value;
      }
    }
  }

  getFormControlId () {
    return this.#inputType === InputType.DATE
      ? this.#formControl.input.id : this.#formControl.id;
  }

  /**
   * Function checks if initial attribute value is undefined and in bulk edit mode
   * (multiple selected features). Initial value is not sent from backend (it's undefined)
   * when selected features have different values for this attribute.
   *
   * @return {boolean} - true means different initial values with multiple features selected.
   * @private
   */
  _isUndefinedValueInBulkEditMode () {
    return this.#isMultipleFeatures && typeof this.#initialValue === 'undefined';
  }

}

customElements.define('d-feature-attribute-form-group', FeatureAttributeFormGroup);
